import {doc} from '../../docs.js';
import {gl_sdf} from './scene.js';
import {lazy} from '../lazy.js';
import {extractCode} from '../extract.js';
import {generateScene} from './scene-generator.js';
import {sdf_union} from './sdf.js';
import {hashCode} from "utils/hashCode.js";
import {uuid} from 'uuidv4';
import {float, extract} from '../utils.js';
import {gl_diffused_light, estimateNormal} from './normals.js';

export const MAX_DISTANCE =80.01;
const MAX_RM_STEPS = 90;
const MIN_DISTANCE = 0.0001;
const EPSILON = MIN_DISTANCE;

doc(
    "gl.raymarching3D",
    {
        "sdfList": "list of sdf objects to be used for ray marching",
        "rayOrigin": "origin of eye (vec3)",
        "fieldOfView": "field of view angle (float)",
    },
    "Creates a new uniform variable with value from left-inlet, in GLSL.",
    () => 3,
    {
        isHot: true,
        tags: "GLSL",
        inlets: 3,
        outlets: ["raymarched distance", "ray direction", "sdf value"],
   });

export const gl_raymarching_3D = (object, rayOrigin, fieldOfView, lightPosition) => {
    object.needsHotReload = true;
    let id = uuid();
    let functionName = "raymarching_" + id.replaceAll('-', '').slice(0,4); 
    let shadowName = "shadow_" + id.replaceAll('-', '').slice(0,4); 
    let normalName = "normal_" + id.replaceAll('-', '').slice(0,4); 
    return ([scene]) => {
        let {codes, uniforms, functions, variables} = extract(scene, rayOrigin, fieldOfView, lightPosition);
        functions[normalName] = {
            args: {
                "pos": "vec3",
            },
            returnType: "vec3",
            body: `
vec2 e = vec2(1.0,-1.0)*0.5773;
    const float eps = 0.0005;
    return normalize( e.xyy*${scene.code("pos + e.xyy*eps")}.x + 
	e.yyx*${scene.code("pos + e.yyx*eps")}.x + 
	e.yxy*${scene.code("pos + e.yxy*eps")}.x + 
	e.xxx*${scene.code("pos + e.xxx*eps")}.x);
`
        };

        functions[shadowName] = {
            args: {
                ro: "vec3" ,
                rd: "vec3",
                tmin: "float",
                tmax: "float",
                k: "float"
            },
            returnType: "float",
            body: `
    float res = 1.0;
    float t = tmin;
    for( int i=0; i<64; i++ )
    {
	float h = ${scene.code('ro + rd*t')}.x;
        res = min( res, k*h/t );
        t += clamp( h, 0.003, 0.10 );
        if( res<0.002 || t>tmax ) break;
    }
    return clamp( res, 0.0, 1.0 );
`
        };

        functions[functionName] = {
            args: {
                "rayOrigin": "vec3",
                "fieldOfView": "float",
                "rayDirection": "vec3",
            },
            returnType: "vec4",
            body: `
float depth = ${float(MIN_DISTANCE)};
float index = -1.0;
vec3 ogRayOrigin = rayOrigin;
float dist = 0.0;

for(int i=0; i<${MAX_RM_STEPS};i++){
    vec2 di =  ${scene.code('rayOrigin + depth*rayDirection')};
    dist = di.x;
    if (dist < ${MIN_DISTANCE}) {
        // need to calculate the shadow 
        index = di.y;
        break;
    }
    if (depth > ${MAX_DISTANCE}) {
        break;
    }
    depth += dist;
}
if (depth < ${MAX_DISTANCE}) {
/*
  vec3 pos = rayOrigin + depth*rayDirection;
  vec3 nor = ${normalName}(pos);
  vec3 lig = ${codes[3]};
  float dif = clamp( dot(nor,lig), 0.0, 1.0 );
  if ( dif>0.001 ) dif *= ${shadowName}( pos+nor*0.001, lig, 0.001, 1.0, 32.0 );
  float amb = 0.5 + 0.5*dot(nor,vec3(0.0,1.0,0.0));
*/
  return vec4(depth, index, 0.0, 0.0);
}
return vec4(${MAX_DISTANCE}, index, 0., 0.);
`
        };
        if (!variables) {
            variables = {};
        }
        variables["rayDir"] = {
            type: "vec3",
            code: `getRayDir(lookAt(${float(codes[1])}, vec3(0.0, 0.0, 0.0), vec3(0.0,0.0,0.5)), ${float(codes[2])})`
        };
        let code =`(${functionName}(${float(codes[1])}, ${float(codes[2])}, rayDir))`; 
        let rayDirection = "rayDir"; //`getRayDir(lookAt(${float(codes[1])}, vec3(0.0, 0.0, 0.0), vec3(0.0,0.0,0.5)), ${float(codes[2])})`;
        let sdfValue = scene.code('vec3((1.0*gl_FragCoord.xy-vec2(width, height))/height, 0.0)');
        return [
            {
                code,
                variables,
                uniforms,
                functions,
                hashCode: hashCode(code)
            },
            {
                code: rayDirection,
                variables,
                uniforms,
                functions,
                hashCode: hashCode(rayDirection)
            },
            {
                code: sdfValue,
                variables,
                uniforms,
                functions,
                hashCode: hashCode(sdfValue)
            }
        ];
    };
};

export const raymarching = {
    "gl.estimateNormal": estimateNormal,
    "gl.raymarching3D": gl_raymarching_3D,
    "gl.sdf": gl_sdf,
    "gl.diffusedLight": gl_diffused_light,
};
